import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid } from 'semantic-ui-react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import FormSelectList from 'components/FormComponents/FormSelect';
import FormInput from 'components/FormComponents/FormInput';
import { handleError } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  AdjustmentReason,
  Throttle,
  ThrottleFormData,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import { GET_OFFER } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import Slider from 'components/FormComponents/Slider';
import { Profile } from 'components/UserMgmt/UserMgmt.helpers';

import UPSERT_THROTTLE from './UpdateThrottle.mutations';
import styles from '../UpdateThrottle.styles';

type Props = {
  offerUuid: string;
  throttle: Throttle;
  updateThrottle: (value: React.SetStateAction<boolean>) => void;
  classes: ClassProp<ReturnType<typeof styles>>;
};

const UpdateThrottle: FunctionComponent<Props> = ({
  offerUuid,
  throttle,
  updateThrottle,
  classes,
}) => {
  const { uuid, name, description } = throttle;
  const [userName, setUserName] = useState<string>();

  const methods = useForm<ThrottleFormData>({
    mode: 'onBlur',
    defaultValues: {
      uuid,
      name,
      description,
      max: throttle.max,
      min: throttle.min,
    },
  });

  const { register, handleSubmit, watch, errors, unregister } = methods;

  const {
    data: { userProfile },
    loading: profileLoading,
  } = useQuery<any>(GET_PROFILE);

  useEffect(() => {
    if (profileLoading) return;
    const { givenName, familyName } = userProfile as Profile;
    setUserName(`${givenName} ${familyName}`);
  }, [userProfile, profileLoading]);

  useEffect(() => {
    register({
      name: 'adjustmentReason',
      required: 'Please select one option',
    });

    return () => unregister('adjustmentReason');
  }, [register]);

  const currentMax = watch('max');
  const currentMin = watch('min');

  const throttleRange = (currentMax + 1 - currentMin) / 10;

  const handleThrottleUpsertResponse = () => {
    updateThrottle(false);
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    UPSERT_THROTTLE,
    {
      onCompleted: handleThrottleUpsertResponse,
      onError: handleError,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) =>
    updateThrottle(false);

  const onSubmit = ({
    uuid,
    adjustmentReason,
    adjustmentReasonDetail,
    adjustmentTicketUrl,
    name,
    description,
    min,
    max,
  }: ThrottleFormData) => {
    // Min and Max represents the percentage of the audience that will see the offer
    // min and max are two between 0 and 999, where min < max
    const currentThrottle = {
      uuid,
      name,
      adjustmentReason,
      adjustmentReasonDetail,
      adjustmentTicketUrl,
      adjustmentUpdatedBy: userName,
      description,
      min,
      max,
    };

    runMutation({
      variables: {
        params: { ...currentThrottle },
      },
    });

    return;
  };

  const reasons = {
    options: [
      {
        key: AdjustmentReason.OVERPACING,
        value: AdjustmentReason.OVERPACING.toUpperCase(),
        text: AdjustmentReason.OVERPACING,
      },
      {
        key: AdjustmentReason.UNDERPACING,
        value: AdjustmentReason.UNDERPACING.toUpperCase(),
        text: AdjustmentReason.UNDERPACING,
      },
      {
        key: AdjustmentReason.OTHER,
        value: AdjustmentReason.OTHER.toUpperCase(),
        text: AdjustmentReason.OTHER,
      },
    ],
    label: {
      children: 'Ajustment Reason',
    },
    placeholder: 'Adjustment Reason',
  };

  const isURL = (adjustmentTicketUrl: string) => {
    return /https:\/\/([\w\-])+\.{1}([a-zA-Z]{2,63})([\/\w-]*)*\/?\??([^#\n\r]*)?#?([^\n\r]*)/.test(
      adjustmentTicketUrl,
    );
  };

  return (
    <Container className={classes.container}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input type="hidden" value={uuid} name="uuid" ref={register} />
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormInput
                  name="name"
                  type="text"
                  placeholder="Enter a name for this throttle"
                  label="Name"
                  register={register}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="description"
                  type="text"
                  placeholder="Brief description for this throttle"
                  label="Description"
                  register={register}
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <Slider
                  name="throttleRange"
                  type="range"
                  placeholder=""
                  label={`Visible to Audience - ${throttleRange}%`}
                  step="1"
                  throttle={throttle}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormSelectList
                  label="Adjustment Reason"
                  name="adjustmentReason"
                  dropDownOptions={reasons}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="adjustmentTicketUrl"
                  type="url"
                  placeholder="https://example.com"
                  label="Adjustment Ticket URL"
                  register={register({
                    validate: {
                      oneOf: (value) =>
                        isURL(value) ||
                        'Please enter a valid Ticket URL describing the adjusment reason',
                    },
                  })}
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <FormInput
                  name="adjustmentReasonDetail"
                  type="text"
                  placeholder="If selected other, please enter reason details"
                  label="Adjustment Reason Detail"
                  register={register({
                    required: 'Provide details for the adjusment reason',
                  })}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0}
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Save'}
              </button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(UpdateThrottle);
